import { useEffect, useState } from "react";
import { Car, Plus, Trophy, MessageSquarePlus, Trash2, Users, Clock, Flag } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, Spinner, EmptyRow } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";

export default function CarClubs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [detected, setDetected] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [d, c] = await Promise.all([
        apiClient.get("/admin/car-clubs/detected-cars"),
        apiClient.get("/admin/car-clubs"),
      ]);
      setDetected(d.data || []);
      setClubs(c.data || []);
    } catch (e) { toast.error(getErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createClub = async (car) => {
    setBusy(car);
    try { await apiClient.post("/admin/car-clubs", { car, name: `${car} Club`, rank_metric: "laps", show_count: 0 }); toast.success(`Created ${car} Club`); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Failed")); }
    finally { setBusy(""); }
  };

  const patch = async (club, upd) => {
    setClubs((prev) => prev.map((c) => (c.id === club.id ? { ...c, ...upd } : c)));
    try { await apiClient.put(`/admin/car-clubs/${club.id}`, upd); }
    catch (e) { toast.error(getErrorMessage(e, "Failed")); load(); }
  };

  const createChannel = async (club) => {
    setBusy(club.id);
    try { const { data } = await apiClient.post(`/admin/car-clubs/${club.id}/create-channel`); toast.success(`Channel ready — invited ${data.invited} fan${data.invited === 1 ? "" : "s"}`); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Failed")); }
    finally { setBusy(""); }
  };

  const remove = async (club) => {
    if (!window.confirm(`Delete the "${club.name}" club? (The chat channel, if any, stays.)`)) return;
    try { await apiClient.delete(`/admin/car-clubs/${club.id}`); toast.success("Club deleted"); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Only an admin can delete")); }
  };

  if (loading) return <Spinner label="Loading car clubs" />;
  const clubbedCars = new Set(clubs.map((c) => c.car));
  const available = detected.filter((d) => !clubbedCars.has(d.car));

  return (
    <div>
      <AdminHeader title="Car Clubs" subtitle="Turn frequently-driven cars into fan clubs with their own leaderboard + chat" />

      {/* Detected cars */}
      <div className="glass rounded-2xl p-5 mb-6" data-testid="detected-cars">
        <h3 className="text-white font-bold mb-1 flex items-center gap-2"><Car className="w-5 h-5 text-cyan-300" /> Detected cars</h3>
        <p className="text-slate-500 text-sm mb-4">Cars drivers have used, ranked by number of fans. Create a club from any of them.</p>
        {available.length === 0 ? (
          <p className="text-slate-500 text-sm">No new cars detected yet — clubs exist for all driven cars, or no telemetry has arrived.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {available.map((d) => (
              <div key={d.car} className="rounded-xl p-4 flex items-center justify-between gap-3" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }} data-testid={`detected-${d.car}`}>
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{d.car}</p>
                  <p className="text-slate-500 text-xs flex items-center gap-2"><Users className="w-3 h-3" />{d.fan_count} fans · {d.total_laps} laps</p>
                </div>
                <button onClick={() => createClub(d.car)} disabled={busy === d.car}
                  className="neon-outline-btn px-3 py-2 text-xs shrink-0" data-testid={`create-club-${d.car}`}>
                  <Plus className="w-3.5 h-3.5" /> Club
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clubs */}
      <TableWrap>
        <thead><Tr><Th>Club</Th><Th>Fans</Th><Th>Rank by</Th><Th>Show</Th><Th>On leaderboard</Th><Th>Chat</Th><Th></Th></Tr></thead>
        <tbody>
          {clubs.length === 0 ? <EmptyRow colSpan={7} text="No clubs yet — create one from a detected car above" /> : clubs.map((c) => (
            <Tr key={c.id} data-testid={`club-${c.id}`}>
              <Td className="text-white font-semibold">{c.name}<div className="text-slate-500 text-xs font-normal">{c.car}</div></Td>
              <Td><span className="inline-flex items-center gap-1 text-slate-200"><Users className="w-3.5 h-3.5 text-slate-500" />{c.fan_count}</span></Td>
              <Td>
                <select value={c.rank_metric} onChange={(e) => patch(c, { rank_metric: e.target.value })}
                  data-testid={`club-metric-${c.id}`} className="bg-slate-900 border border-white/10 rounded-lg px-2 py-1 text-slate-200">
                  <option value="laps" className="bg-slate-900">Most laps</option>
                  <option value="hours" className="bg-slate-900">Most hours</option>
                </select>
              </Td>
              <Td>
                <select value={c.show_count} onChange={(e) => patch(c, { show_count: parseInt(e.target.value, 10) })}
                  data-testid={`club-show-${c.id}`} className="bg-slate-900 border border-white/10 rounded-lg px-2 py-1 text-slate-200">
                  <option value={0} className="bg-slate-900">All</option>
                  <option value={5} className="bg-slate-900">Top 5</option>
                  <option value={10} className="bg-slate-900">Top 10</option>
                  <option value={20} className="bg-slate-900">Top 20</option>
                </select>
              </Td>
              <Td>
                <button onClick={() => patch(c, { leaderboard_enabled: !c.leaderboard_enabled })}
                  data-testid={`club-enabled-${c.id}`}
                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                  style={{ background: c.leaderboard_enabled ? "#00e5ff" : "rgba(120,150,220,0.25)" }}>
                  <span className="inline-block h-5 w-5 rounded-full bg-white transition-transform" style={{ transform: c.leaderboard_enabled ? "translateX(22px)" : "translateX(2px)" }} />
                </button>
              </Td>
              <Td>
                <button onClick={() => createChannel(c)} disabled={busy === c.id}
                  className="neon-outline-btn px-2.5 py-1.5 text-xs" data-testid={`club-channel-${c.id}`}>
                  <MessageSquarePlus className="w-3.5 h-3.5" /> {c.chat_room_id ? "Sync fans" : "Create channel"}
                </button>
              </Td>
              <Td>
                {isAdmin && (
                  <button onClick={() => remove(c)} className="text-slate-500 hover:text-red-400" data-testid={`club-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
      </TableWrap>

      <p className="text-slate-500 text-xs mt-4 flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> Fans are anyone who has driven the car.</span>
        <span className="inline-flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> "Rank by" sets the top-fan metric.</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Changes save automatically.</span>
      </p>
    </div>
  );
}
